import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";

import Editor from "./editor";

import Form from "../../common/form";
import Expire from "../../common/expire";
import FormHeader from "../../common/form/formHeader";
import FormFooter from "../../common/form/formFooter";

import { getFiles } from "../../../services/fileService";
import { getEntities, saveEntity } from "../../../services/apiService";

class Settings extends Form {
  state = {
    data: {
      _id: undefined,
      name: "Histocaching",
      description_en: "Powered by CGV @ TU Dresden",
      description_de: "Powered by CGV @ TU Dresden",
      info_en: "",
      info_de: "",
      help_en: "",
      help_de: "",
      has_files_module: true,
      has_folders_module: true,
      has_landing_module: true,
      has_gallery_module: true,
      has_overview_module: true,
      has_users_module: true,
    },
    files: [],
    errors: {},
  };

  schema = {
    _id: Joi.string(),
    name: Joi.string()
      .min(3)
      .max(100)
      .default("Histocaching")
      .required()
      .label("Name"),
    description_en: Joi.string()
      .min(3)
      .max(500)
      .default("Powered by CGV @ TU Dresden")
      .required()
      .label("Description"),
    description_de: Joi.string()
      .min(3)
      .max(500)
      .default("Powered by CGV @ TU Dresden")
      .required()
      .label("Beschreibung"),

    info_en: Joi.string().allow("").label("Information"),
    info_de: Joi.string().allow("").label("Informationen"),
    help_en: Joi.string().allow("").label("Help"),
    help_de: Joi.string().allow("").label("Hilfe"),

    has_files_module: Joi.boolean()
      .default(true)
      .required()
      .label("Files Module"),
    has_folders_module: Joi.boolean()
      .default(true)
      .required()
      .label("Folders Module"),
    has_landing_module: Joi.boolean()
      .default(true)
      .required()
      .label("Landing Page"),
    has_gallery_module: Joi.boolean().default(true).required().label("Gallery"),
    has_overview_module: Joi.boolean()
      .default(true)
      .required()
      .label("Overview Module"),
    has_users_module: Joi.boolean()
      .default(true)
      .required()
      .label("Users Module"),
  };

  // Retrive the Data from the Server
  async componentDidMount() {
    // Get Files
    const { data: files } = await getFiles("public");
    if (files.length === 0) return;

    // Get Setting
    const { data: setting } = await getEntities("setting");
    if (!setting) return this.props.history.replace("/not-found");

    this.setState({
      data: this.mapToViewModel(setting),
      files: files,
    });
  }

  // Map current Poi to the Form Inputs
  mapToViewModel(setting) {
    return {
      _id: setting._id,
      name: setting.name,
      description_en: setting.description_en,
      description_de: setting.description_de,
      info_en: setting.info_en,
      info_de: setting.info_de,
      help_en: setting.help_en,
      help_de: setting.help_de,
      has_files_module: setting.has_files_module,
      has_folders_module: setting.has_folders_module,
      has_landing_module: setting.has_landing_module,
      has_gallery_module: setting.has_gallery_module,
      has_overview_module: setting.has_overview_module,
      has_users_module: setting.has_users_module,
    };
  }

  doSubmit = async () => {
    // Save Item to the Database
    try {
      const response = await saveEntity("setting", this.state.data);

      if (response.data && response.status === 200) {
        toast.success("Setting saved successfully.");
      }
    } catch (ex) {
      if (ex.response && ex.response.status === 400) {
        toast.error("Setting can not be saved!");
      }
      return;
    }

    window.location = "/dashboard/overview";
  };

  // Back button Event Handler
  handleBack = () => {
    this.props.history.push("/dashboard/overview");
  };

  editorHandleChange = (name, content) => {
    const data = { ...this.state.data };
    data[name] = content;
    this.setState({ data });
  };

  render() {
    const { _id, info_de, info_en, help_de, help_en } = this.state.data;

    return (
      <div className="col alt-table-responsive">
        <FormHeader name="Settings" icon="bx bx-cog" id={_id ?? "setting"} />
        <div className="row py-2 my-2 align-items-center">
          <div className="col-md-4 col-sm-6">
            <div className="row">
              <div className="col text-center">
                <img
                  src={`${process.env.PUBLIC_URL}/logo512.png`}
                  alt="logo"
                  style={{ height: "290px" }}
                />
              </div>
            </div>
          </div>
          <div className="col-md-8 col-sm-6">
            <div className="row">
              <div className="col">{this.renderInput("name", "Name")}</div>
            </div>
            <div className="row">
              <div className="col">
                {this.renderTextBox("description_en", "Description")}
              </div>
            </div>
            <div className="row">
              <div className="col">
                {this.renderTextBox("description_de", "Beschreibung")}
              </div>
            </div>
          </div>
        </div>

        <div className="accordion" id="settingsAccordion">
          <div className="card">
            <div className="card-header" id="headingTwo">
              <h2 className="mb-0">
                <button
                  className="btn text-left btn-link collapsed shadow-none"
                  type="button"
                  data-toggle="collapse"
                  data-target="#collapseInfo"
                  aria-expanded="false"
                  aria-controls="collapseInfo"
                >
                  <span className="font-weight-bold">
                    <i className="bx bx-plus bx-sm align-bottom"></i>Info
                  </span>
                </button>
              </h2>
            </div>
            <div
              id="collapseInfo"
              className="collapse"
              aria-labelledby="headingTwo"
              data-parent="#settingsAccordion"
            >
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h3>Information</h3>
                    <Editor
                      label={"Info English"}
                      name={"info_en"}
                      value={info_en}
                      onChange={this.editorHandleChange}
                    />
                  </div>
                  <div className="col-md-6 border-md-left mt-sm-4 mt-md-0">
                    <h3>Informationen</h3>
                    <Editor
                      label={"Info Deutsch"}
                      name={"info_de"}
                      value={info_de}
                      onChange={this.editorHandleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header" id="headingThree">
              <h2 className="mb-0">
                <button
                  className="btn btn-link text-left collapsed shadow-none"
                  type="button"
                  data-toggle="collapse"
                  data-target="#collapseHelp"
                  aria-expanded="false"
                  aria-controls="collapseHelp"
                >
                  <span className="font-weight-bold">
                    <i className="bx bx-plus bx-sm align-bottom"></i>Help
                  </span>
                </button>
              </h2>
            </div>
            <div
              id="collapseHelp"
              className="collapse"
              aria-labelledby="headingThree"
              data-parent="#settingsAccordion"
            >
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h3>Help</h3>
                    <Editor
                      label={"Help English"}
                      name={"help_en"}
                      value={help_en}
                      onChange={this.editorHandleChange}
                    />
                  </div>
                  <div className="col-md-6 border-md-left mt-sm-4 mt-md-0">
                    <h3>Hilfe</h3>
                    <Editor
                      label={"Help Deutsch"}
                      value={help_de}
                      name={"help_de"}
                      onChange={this.editorHandleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header" id="headingOne">
              <h2 className="mb-0">
                <button
                  className="btn btn-link text-left shadow-none collapsed"
                  type="button"
                  data-toggle="collapse"
                  data-target="#collapseModules"
                  aria-expanded="true"
                  aria-controls="collapseModules"
                >
                  <span className="font-weight-bold">
                    <i className="bx bx-plus bx-sm align-bottom"></i>Modules
                  </span>
                </button>
              </h2>
            </div>

            <div
              id="collapseModules"
              className="collapse"
              aria-labelledby="headingOne"
              data-parent="#settingsAccordion"
            >
              <div className="card-body">
                <div className="row">
                  <div className="col-sm-4 my-2">
                    {this.renderToggle("has_files_module", "Files Module")}
                  </div>
                  <div className="col-sm-4 my-2">
                    {this.renderToggle("has_folders_module", "Folders Module")}
                  </div>
                  <div className="col-sm-4 my-2">
                    {this.renderToggle("has_landing_module", "Landing Page")}
                  </div>
                  <div className="col-sm-4 my-2">
                    {this.renderToggle("has_gallery_module", "Gallery")}
                  </div>
                  <div className="col-sm-4 my-2">
                    {this.renderToggle("has_users_module", "Users Module")}
                  </div>
                  <div className="col-sm-4 my-2">
                    {this.renderToggle(
                      "has_overview_module",
                      "Overview Module"
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {_id ? (
          <form onSubmit={this.handleSubmit} className="form-group">
            <FormFooter
              onBackClick={this.handleBack}
              onValidate={this.validate()}
            />
          </form>
        ) : (
          <div className="row">
            <div className="col-sm-12 align-self-center">
              <Expire delay={5000}>
                <div className="loader">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
              </Expire>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default Settings;
